import React, { useState, useEffect } from 'react';
import ErrorBoundary from '../../../../Utility/Components/ErrorBoundary';
import Utility from '../../../../Utility/Utility';
function ConsumedItems(props) {
    const {
        currencyName,
        consumedItemsData = {},
        excludeTax,
        is_igst_enable = false
    } = props;

    // State for handling IGST toggle when not provided via props
    const [localIgstMode, setLocalIgstMode] = useState(is_igst_enable);

    // Use either the prop value if provided or local state
    const effectiveIgstMode = is_igst_enable || localIgstMode;

    // State for consumed items
    const [consumedItems, setConsumedItems] = useState([]);

    // SAC codes and GST rates mapping
    const sacCodes = {
        '996311': { description: 'Room accommodation services', rate: 18 },
        '996331': { description: 'Restaurant and café services', rate: 5 },
        '996332': { description: 'Catering services', rate: 18 },
        '996334': { description: 'Room service', rate: 18 },
        '997321': { description: 'Cleaning services', rate: 18 },
        '996711': { description: 'Laundry services', rate: 18 },
        '996719': { description: 'Other services', rate: 18 }
    };

    // Calculate subtotals for display
    const calculateSubtotals = () => {
        // Initialize subtotals
        let subtotalAmount = 0;
        let subtotalCGST = 0;
        let subtotalSGST = 0;
        let subtotalIGST = 0;
        let subtotalTotal = 0;

        // Calculate subtotals based on all items
        consumedItems.forEach(item => {
            subtotalAmount += item.amount;

            if (!excludeTax) {
                if (effectiveIgstMode) {
                    subtotalIGST += item.igst;
                } else {
                    subtotalCGST += item.cgst;
                    subtotalSGST += item.sgst;
                }
            }

            subtotalTotal += item.total;
        });

        return {
            subtotalAmount,
            subtotalCGST,
            subtotalSGST,
            subtotalIGST,
            subtotalTotal
        };
    };

    // Process API data format for consumption and damage items
    const processApiData = (apiData) => {
        if (!apiData) return [];
        const mappedItems = [];
        let counter = 1;

        // Process consumption items
        if (apiData.consumption_item && apiData.consumption_item.length > 0) {
            const consumptionItems = apiData.consumption_item.map(item => {
                const amount = parseFloat(item.quantity_used) * parseFloat(item.selling_cost) || 0;
                const gst_percentage = (item.sgst_perc + item.cgst_perc) || 0;

                let cgst = 0;
                let sgst = 0;
                let igst = 0;
                let total = amount;

                // Only calculate tax if not excluded
                if (!excludeTax) {
                    const gst_amount = amount * (gst_percentage / 100);

                    if (effectiveIgstMode) {
                        igst = gst_amount;
                    } else {
                        cgst = gst_amount / 2;
                        sgst = gst_amount / 2;
                    }

                    total = amount + (effectiveIgstMode ? igst : cgst + sgst);
                }

                return {
                    id: counter++,
                    item_id: item.item_id,
                    category_id: item.category_id,
                    description: item.item_name || '',
                    quantity: item.quantity_used || 1,
                    rate: item.selling_cost || 0,
                    amount,
                    gst_percentage,
                    cgst,
                    sgst,
                    igst,
                    total,
                    sac_code: item.sac_code || '',
                    item_type: 'consumption',
                    unit: item.unit || ''
                };
            });

            mappedItems.push(...consumptionItems);
        }

        // Process damage items
        if (apiData.damage_item && apiData.damage_item.length > 0) {
            const damageItems = apiData.damage_item.map(item => {
                const amount = parseFloat(item.quantity) * parseFloat(item.charge_per_unit) || 0;

                return {
                    id: counter++,
                    item_id: item.item_id,
                    category_id: item.category_id,
                    description: `${item.item_name} (Damaged)` || '',
                    quantity: item.quantity || 1,
                    rate: item.charge_per_unit || 0,
                    amount,
                    gst_percentage: 0, // Damage items might not have tax
                    cgst: 0,
                    sgst: 0,
                    igst: 0,
                    total: amount,
                    sac_code: '', // Would need to be mapped if available
                    item_type: 'damage',
                    unit: item.unit || ''
                };
            });

            mappedItems.push(...damageItems);
        }

        return mappedItems;
    };

    // Use the consumedItemsData from props if available
    useEffect(() => {
        // console.log('consumedItemsData', consumedItemsData)
       
            // Check if the data might be in the API format
            if (consumedItemsData.consumption_item || consumedItemsData.damage_item) {
                // Process data in API format
                const processedItems = processApiData(consumedItemsData);
                // console.log('processedItems', processedItems)

                setConsumedItems(processedItems);
            } 
        
    }, [consumedItemsData, effectiveIgstMode, excludeTax]);

    // Update local IGST mode when prop changes
    useEffect(() => {
        setLocalIgstMode(is_igst_enable);
    }, [is_igst_enable]);

    const subtotals = calculateSubtotals();

    return (
        <ErrorBoundary title="Consumed Items Error">
            <div className="consumed-items-container">
                <div className="consumed-items-table-container">
                    <table className="consumed-items-table table table-bordered">
                        <thead>
                            <tr>
                                <th>Sl. No.</th>
                                <th>Item Description</th>
                                <th>Type</th>
                                <th>Qty</th>
                                <th>Unit</th>
                                <th>Rate ({Utility.getCurrencySymbol(currencyName)})</th>
                                <th>Amount ({Utility.getCurrencySymbol(currencyName)})</th>
                                {!excludeTax && <th>GST (%)</th>}
                                {!excludeTax && effectiveIgstMode ? (
                                    <th>IGST ({Utility.getCurrencySymbol(currencyName)})</th>
                                ) : (
                                    <>
                                        {!excludeTax && <th>CGST ({Utility.getCurrencySymbol(currencyName)})</th>}
                                        {!excludeTax && <th>SGST ({Utility.getCurrencySymbol(currencyName)})</th>}
                                    </>
                                )}
                                <th>Total ({Utility.getCurrencySymbol(currencyName)})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {consumedItems.map((item, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.description}</td>
                                    <td>{item.item_type || 'consumption'}</td>
                                    <td>{item.quantity}</td>
                                    <td>{item.unit || ''}</td>
                                    <td>{Utility.getCurrencyValue(item.rate)}</td>
                                    <td>{Utility.getCurrencyValue(item.amount)}</td>
                                    {!excludeTax && <td>{item.gst_percentage}%</td>}
                                    {!excludeTax && effectiveIgstMode ? (
                                        <td>{Utility.getCurrencyValue(item.igst)}</td>
                                    ) : (
                                        <>
                                            {!excludeTax && <td>{Utility.getCurrencyValue(item.cgst)}</td>}
                                            {!excludeTax && <td>{Utility.getCurrencyValue(item.sgst)}</td>}
                                        </>
                                    )}
                                    <td>{Utility.getCurrencyValue(item.total)}</td>
                                </tr>
                            ))}
                            <tr className="subtotal-row">
                                <td colSpan={6}></td>
                                <td>{Utility.getCurrencyValue(subtotals.subtotalAmount)}</td>
                                {!excludeTax && <td></td>}
                                {!excludeTax && effectiveIgstMode ? (
                                    <td>{Utility.getCurrencyValue(subtotals.subtotalIGST)}</td>
                                ) : (
                                    <>
                                        {!excludeTax && <td>{Utility.getCurrencyValue(subtotals.subtotalCGST)}</td>}
                                        {!excludeTax && <td>{Utility.getCurrencyValue(subtotals.subtotalSGST)}</td>}
                                    </>
                                )}
                                <td>{Utility.getCurrencyValue(subtotals.subtotalTotal)}</td>
                            </tr>
                            <tr className="total-row">
                                <td colSpan={6} className="subtotal-label">Total</td>
                                <td colSpan={excludeTax ? 2 : (effectiveIgstMode ? 3 : 5)} className="subtotal-value">
                                    {Utility.getCurrencyValue(subtotals.subtotalTotal)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </ErrorBoundary>
    );
}

export default ConsumedItems;
